import type { SnackbarKey } from 'notistack';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import type { AiTransformationStatus, AiTransformationSubject } from '../../data-contract/ai-transformation-strategy';
import type { AiTransformationFilters } from '../../fetchers/position-management/ai-transformation-fetchers';
import { unwrapResult } from '../../fetchers/unwrap-result';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { useLocale } from '../components/use-locale';
import { useRefreshStrategyReads } from './api/use-move-strategy-items';
import { nextItemsPage } from './api/use-strategy-status-column';
import type { PriorityToastAction } from './components/PriorityToast';
import { PriorityToast } from './components/PriorityToast';
import type { PrioritizeMoved, PrioritizeResult, PrioritizeScope, StrategyMoves } from './model/prioritization';
import { prioritize, prioritizeOtherTasks, undoPrioritize, unprioritize } from './model/prioritization';
import type { PriorityTarget } from './priority-overrides';
import { clearPriorityOverride, isPriorityChanging, setPriorityOverride } from './priority-overrides';

/** The largest page the items endpoint serves. */
const ITEMS_PAGE_SIZE = 200;
const TOAST_DURATION_MS = 10_000;

const useStrategyMoves = (architectureId: string): StrategyMoves => {
    const { fetchAiTransformationItems, fetchAiTransformationSummary, updateAiTransformationStatuses } =
        useApplicationFetchers();
    const acceptLanguage = useLocale();

    return useMemo(() => {
        const listPage = async (
            input: Parameters<StrategyMoves['listMovable']>[0],
            page: number,
        ): Promise<readonly string[]> => {
            const response = await unwrapResult(
                fetchAiTransformationItems({
                    acceptLanguage,
                    architectureId,
                    filters: input.filters,
                    page,
                    size: ITEMS_PAGE_SIZE,
                    statuses: [input.from],
                    subject: input.subject,
                }),
            );
            const ids = response.items
                .filter((item) => item.status === input.from && item.movableTo.includes(input.to))
                .map(({ id }) => id);
            const next = nextItemsPage(response);

            return next === undefined ? ids : [...ids, ...(await listPage(input, next))];
        };

        return {
            count: async ({ filters, status, subject }) => {
                const summary = await unwrapResult(
                    fetchAiTransformationSummary({ acceptLanguage, architectureId, filters, subject }),
                );

                return summary.statusCounts.find((entry) => entry.status === status)?.count ?? 0;
            },
            listMovable: (input) => listPage(input, 0),
            move: async ({ filters, ids, status, subject }) => {
                const response = await unwrapResult(
                    updateAiTransformationStatuses({ architectureId, filters, request: { ids, status, subject } }),
                );

                return response.updated;
            },
        };
    }, [
        acceptLanguage,
        architectureId,
        fetchAiTransformationItems,
        fetchAiTransformationSummary,
        updateAiTransformationStatuses,
    ]);
};

const hasMoved = ({ positionIds, taskIds }: PrioritizeMoved): boolean => positionIds.length + taskIds.length > 0;

/**
 * Prioritizes a role, level, position, task or matrix selection in one action and takes priorities away again. A
 * prioritization says what it moved in a toast that offers the scope's other assessed tasks and an undo; every change
 * refreshes the page's reads afterwards, whatever its outcome.
 *
 * A change made on a target's control shows on every control of that target at once and holds until the refreshed
 * reads arrive; a failed one is taken back as its error shows. A second click while it is saved is ignored.
 */
export const useStrategyPriority = (architectureId: string) => {
    const moves = useStrategyMoves(architectureId);
    const refreshStrategyReads = useRefreshStrategyReads();
    const translate = useTranslate();
    const { closeSnackbar, enqueueSnackbar } = useSnackbar();
    const { showErrorMessageSnackbar } = useSnackbars();
    const [running, setRunning] = useState(0);

    const run = useCallback(
        async <T,>(work: () => Promise<T>): Promise<T | undefined> => {
            setRunning((count) => count + 1);
            try {
                return await work();
            } catch {
                showErrorMessageSnackbar('pages.ai-transformation.strategy.priority.error');

                return undefined;
            } finally {
                setRunning((count) => count - 1);
                await refreshStrategyReads();
            }
        },
        [refreshStrategyReads, showErrorMessageSnackbar],
    );

    const runOptimistic = useCallback(
        async <T,>(
            target: PriorityTarget | undefined,
            prioritized: boolean,
            work: () => Promise<T>,
        ): Promise<T | undefined> => {
            if (target === undefined) return run(work);
            if (isPriorityChanging(target)) return undefined;
            setPriorityOverride(target, prioritized);
            try {
                return await run(async () => {
                    try {
                        return await work();
                    } catch (error) {
                        clearPriorityOverride(target);
                        throw error;
                    }
                });
            } finally {
                clearPriorityOverride(target);
            }
        },
        [run],
    );

    const toast = useCallback(
        (text: string, actions: readonly PriorityToastAction[]) => {
            const key: SnackbarKey = enqueueSnackbar(
                <PriorityToast
                    actions={actions.map((action) => ({
                        ...action,
                        onClick: () => {
                            closeSnackbar(key);
                            action.onClick();
                        },
                    }))}
                    text={text}
                />,
                { autoHideDuration: TOAST_DURATION_MS, variant: 'success' },
            );
        },
        [closeSnackbar, enqueueSnackbar],
    );

    const undoAction = useCallback(
        (scope: PrioritizeScope, moved: PrioritizeMoved): PriorityToastAction => ({
            label: translate('pages.ai-transformation.strategy.priority.undo'),
            onClick: () => void run(() => undoPrioritize(moves, scope, moved)),
        }),
        [moves, run, translate],
    );

    const addOtherTasks = useCallback(
        async (scope: PrioritizeScope, result: PrioritizeResult) => {
            const added = await run(() => prioritizeOtherTasks(moves, scope));
            if (added === undefined) return;
            const moved = { positionIds: result.positionIds, taskIds: [...result.taskIds, ...added] };
            toast(
                translate('pages.ai-transformation.strategy.priority.prioritized-all-tasks', {
                    count: moved.taskIds.length,
                    name: scope.name,
                }),
                [undoAction(scope, moved)],
            );
        },
        [moves, run, toast, translate, undoAction],
    );

    const textOf = (scope: PrioritizeScope, result: PrioritizeResult): string => {
        if (scope.tasks === undefined) {
            return translate('pages.ai-transformation.strategy.priority.prioritized', { name: scope.name });
        }
        if (!scope.tasks.onlyHighPotential) {
            return translate('pages.ai-transformation.strategy.priority.prioritized-tasks', {
                count: result.taskIds.length,
            });
        }

        return result.taskIds.length > 0
            ? translate('pages.ai-transformation.strategy.priority.prioritized-with-tasks', {
                  count: result.taskIds.length,
                  name: scope.name,
              })
            : translate('pages.ai-transformation.strategy.priority.prioritized-without-tasks', { name: scope.name });
    };

    const prioritizeScope = async (scope: PrioritizeScope, target?: PriorityTarget): Promise<void> => {
        const result = await runOptimistic(target, true, () => prioritize(moves, scope));
        if (result === undefined || (!hasMoved(result) && result.otherTaskCount === 0)) return;
        const offer: readonly PriorityToastAction[] =
            result.otherTaskCount > 0
                ? [
                      {
                          label: translate(
                              result.taskIds.length > 0
                                  ? 'pages.ai-transformation.strategy.priority.add-other-tasks'
                                  : 'pages.ai-transformation.strategy.priority.add-assessed-tasks',
                              { count: result.otherTaskCount },
                          ),
                          onClick: () => void addOtherTasks(scope, result),
                      },
                  ]
                : [];
        toast(textOf(scope, result), hasMoved(result) ? [...offer, undoAction(scope, result)] : offer);
    };

    const unprioritizeScope = async (scope: PrioritizeScope, target?: PriorityTarget): Promise<void> => {
        await runOptimistic(target, false, () => unprioritize(moves, scope));
    };

    /** Moves one item between Idle and Prioritized on its own, as a task is; a move the backend skips fails. */
    const moveItem = async (
        item: { readonly id: string; readonly subject: AiTransformationSubject },
        status: Extract<AiTransformationStatus, 'IDLE' | 'PRIORITIZED'>,
        filters: AiTransformationFilters,
        target: PriorityTarget,
    ): Promise<void> => {
        await runOptimistic(target, status === 'PRIORITIZED', async () => {
            const updated = await moves.move({ filters, ids: [item.id], status, subject: item.subject });
            if (updated < 1) throw new Error(`Moved none of item ${item.id}`);
        });
    };

    return { isPending: running > 0, moveItem, prioritize: prioritizeScope, unprioritize: unprioritizeScope };
};
